import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';

import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import type {
  FinanceOverviewFilters,
  FinanceOverviewResponse,
  FinanceProjectsFilters,
  FinanceProjectsResponse,
  FinanceWatchFilters,
  FinanceWatchResponse,
} from '../types/finances';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  overview: 'No pudimos cargar el resumen financiero. Inténtalo de nuevo en unos segundos.',
  projects: 'No pudimos cargar los proyectos financieros. Inténtalo de nuevo en unos segundos.',
  watch: 'No pudimos cargar la vigilancia financiera. Inténtalo de nuevo en unos segundos.',
} as const;

export function useFinancesApi() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de finanzas');
      }

      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/finances${config.url ?? ''}`,
        });
        return response.data;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          throw new Error(ERROR_MESSAGES.unauthorized);
        }
        throw error;
      }
    },
    [token],
  );

  const fetchOverview = useCallback(
    async (filters: FinanceOverviewFilters) => {
      try {
        return await request<FinanceOverviewResponse>({
          method: 'GET',
          url: '/overview',
          params: filters,
        });
      } catch (error) {
        if (error instanceof Error && error.message === ERROR_MESSAGES.unauthorized) throw error;
        throw new Error(ERROR_MESSAGES.overview);
      }
    },
    [request],
  );

  const fetchProjects = useCallback(
    async (filters: FinanceProjectsFilters) => {
      try {
        return await request<FinanceProjectsResponse>({
          method: 'GET',
          url: '/projects',
          params: filters,
        });
      } catch (error) {
        if (error instanceof Error && error.message === ERROR_MESSAGES.unauthorized) throw error;
        throw new Error(ERROR_MESSAGES.projects);
      }
    },
    [request],
  );

  const fetchWatch = useCallback(
    async (filters: FinanceWatchFilters) => {
      try {
        return await request<FinanceWatchResponse>({
          method: 'GET',
          url: '/watch',
          params: filters,
        });
      } catch (error) {
        if (error instanceof Error && error.message === ERROR_MESSAGES.unauthorized) throw error;
        throw new Error(ERROR_MESSAGES.watch);
      }
    },
    [request],
  );

  return {
    fetchOverview,
    fetchProjects,
    fetchWatch,
  };
}
